// Toast copy for the Job Setup "Save & Send" result.
//
// setup-send answers 200 even when a track fails — the verdict is per track. The
// backend's user_error_msg is the only thing separating a retryable outage
// (contract.boldsign_unavailable) from an account-level failure no retry can fix
// (contract.boldsign_permanent_failure) or a payload problem the inspector must
// correct on the Job (contract.boldsign_rejected_request), so always prefer it
// over generic copy. Pure so the matrix stays unit-testable.

/**
 * Did the Contract Sent email fail to reach the client?
 *
 * That email is the client's only signing invite — BoldSign's own emails are
 * switched off (backend #243) — so the contract exists but nobody has been
 * asked to sign it. Two distinct
 * signals: `skip_reason` is Morph declining to send (empty template, no email
 * grant, template switched off), while `dispatched: false` is the send itself
 * not going out (a broker outage). Checking only `skip_reason` reads a broker
 * outage as success.
 */
export const isContractEmailFailed = (contractEmail) =>
  Boolean(contractEmail?.skip_reason) || contractEmail?.dispatched === false;

const CONTRACT_FALLBACK = "Contract failed to send.";
const PAYMENT_FALLBACK = "Payment request failed to send.";

const failure = (outcome, fallback) => ({
  message: outcome?.message || fallback,
  variant: "error",
});

export const buildSendMessages = ({ result, wantContract, wantPayment }) => {
  const messages = [];

  if (wantContract) {
    const contract = result?.contract;
    if (!contract?.ok) {
      messages.push(failure(contract, CONTRACT_FALLBACK));
    } else if (contract.emailSkipped) {
      // The contract exists, but its email — the client's only signing invite —
      // did not go out. A warning rather than a failure: the inspector can still
      // forward the link with Copy Content.
      messages.push({
        message:
          "Contract created — the client wasn't emailed the signing link.",
        variant: "warning",
      });
    } else {
      messages.push({ message: "Contract sent.", variant: "success" });
    }
  }

  if (wantPayment) {
    const payment = result?.payment;
    // "created", not "sent". The payment track's _dispatch_email only prepares
    // a draft EmailLog; the Stripe link exists but the client has not been
    // emailed. Reporting a send that did not happen is worse than a wordier
    // toast — the inspector would never think to follow up.
    messages.push(
      payment?.ok
        ? {
            message: "Payment request created — not emailed yet.",
            variant: "warning",
          }
        : failure(payment, PAYMENT_FALLBACK)
    );
  }

  return messages;
};
